import axios from "axios";
import { toast } from "sonner";
import { resolveErrorMessage } from "../errors/catalog";

// Base URL: permite configurar en .env (REACT_APP_API_URL). Si no, usa /api.
const baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

const api = axios.create({
  baseURL,
});

// ---------------------------------------------------------------------------
// Request interceptor: añade el JWT en cada request y aplica la bandera
// `suppressGlobalToast` automáticamente a las requests de la familia Planner
// (D4 — defensa en profundidad, profundidad b).
//
// URL-prefix + method whitelist cubre las 11+ calls directos en
// Planner.js:533, 782, 799, 914, 1038, 1063, 1096, 1126, 1148, 1179, 2389
// (incluye PUT /bookings/:id y POST /weekly-templates/:id/apply que el
// patrón original se dejaba fuera). Las Planner reads que ya pasan por
// `plannerRequestConfig` también llevan el flag (lo activa ese helper).
// ---------------------------------------------------------------------------

const PLANNER_URL_PREFIXES = ["/breaks", "/bookings", "/waitlist", "/weekly-templates"];
const PLANNER_MUTATION_METHODS = new Set(["post", "put", "patch", "delete"]);

function isPlannerRequest(config) {
  if (!config) return false;
  if (config.source === "planner") return true;
  const url = typeof config.url === "string" ? config.url : "";
  if (!PLANNER_MUTATION_METHODS.has((config.method || "get").toLowerCase())) {
    return false;
  }
  return PLANNER_URL_PREFIXES.some((prefix) => url === prefix || url.startsWith(`${prefix}/`) || url.startsWith(`${prefix}?`));
}

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("adamsched_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.suppressGlobalToast !== true && isPlannerRequest(config)) {
    config.suppressGlobalToast = true;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Module-level reference for the `session_expired` callback. AuthContext
// (see contexts/AuthContext.js) registers/unregisters a closure that calls
// `invalidateLocalCredentials()` + `navigate('/login')`. The response
// interceptor reads it on every `session_expired` error without coupling to
// React (D5).
// ---------------------------------------------------------------------------

export const sessionExpiredHandlerRef = { current: null };

// Clasifica el modo de fallo de una request según la matriz D3. Función pura,
// sin efectos. Distingue tres modos:
//  - 'canceled'  : cancelación intencional (AbortController de D1). Neutral: no
//                  banner, no toast, no retry, no cambio de estado visible.
//  - 'transient' : network error (sin respuesta), timeout axios (ECONNABORTED),
//                  o HTTP 500/502/503/504/429. Abre el estado recuperable.
//  - 'permanent' : cualquier otro HTTP con respuesta (4xx != 429, 401/403, …).
//                  Conserva la semántica actual (toast/flujo existente).
export function classifyRequestError(error) {
  if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
    return "canceled";
  }
  const status = error?.response?.status;
  if (status === undefined) {
    // Sin respuesta del backend: network error o timeout (ECONNABORTED).
    return "transient";
  }
  if (
    status === 408 ||
    status === 429 ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504
  ) {
    return "transient";
  }
  return "permanent";
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Guard 1: Planner resilience — canceled/transient no emiten toast.
    const mode = classifyRequestError(error);
    if (mode === "canceled" || mode === "transient") {
      return Promise.reject(error);
    }

    // session_expired → invalidateLocalCredentials + redirect to /login.
    // This MUST run before the suppressGlobalToast guard: a Planner
    // mutation (auto-flagged with suppressGlobalToast=true) can still
    // come back with a 401 session_expired, and the user must be logged
    // out regardless of who "owns" the toast (D5).
    if (error?.response?.data?.error_code === "session_expired") {
      sessionExpiredHandlerRef.current?.();
    }

    // Guard 2: el call site ya emite su propio toast (Planner, Layout, ...).
    // Resolvemos la traducción y la dejamos en `error.translated` por si
    // el call site la quiere reuse (logging, telemetría futura), pero NO
    // emitimos un toast global — eso evitaría el doble toast (D4).
    if (error?.config?.suppressGlobalToast === true) {
      const localeForReuse =
        (typeof window !== "undefined" &&
          window.localStorage &&
          window.localStorage.getItem("adamsched_language")) ||
        "es";
      const resolvedForReuse = resolveErrorMessage(error, localeForReuse);
      if (resolvedForReuse) {
        error.translated = resolvedForReuse;
      }
      return Promise.reject(error);
    }

    // El interceptor vive fuera de React. AuthContext persiste `language`
    // en `localStorage.adamsched_language`; leemos de ahí en el momento
    // del error — sin suscribirnos a React state.
    const locale =
      (typeof window !== "undefined" &&
        window.localStorage &&
        window.localStorage.getItem("adamsched_language")) ||
      "es";
    const resolved = resolveErrorMessage(error, locale);
    if (resolved) {
      toast.error(resolved.text);
      error.translated = resolved;
    }

    // Nunca swallow. El call site sigue recibiendo el error.
    return Promise.reject(error);
  }
);

export default api;